import React from 'react';
import { BlobProvider, Document, Font, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';

Font.register({
  family: 'Amiri',
  fonts: [
    { src: '/fonts/Amiri-Regular.ttf' },
    { src: '/fonts/Amiri-Bold.ttf', fontWeight: 'bold' }
  ]
});

// Generic PDF renderer for the schema-driven custom forms: mirrors whatever
// sections/rows/columns the form's own schema defines, so one component covers
// every form type instead of a bespoke layout per type.
const styles = StyleSheet.create({
  page: {
    backgroundColor: '#FFFFFF',
    paddingBottom: 36,
  },
  header: {
    margin: 20,
    marginBottom: 10,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerText: {
    fontSize: 16,
    textTransform: 'uppercase',
  },
  subHeaderText: {
    fontSize: 10,
    color: '#8c8c8c',
    marginTop: 4,
  },
  section: {
    margin: 20,
    marginTop: 0,
    marginBottom: 14,
    border: 1,
    borderColor: '#e8e8e8',
    boxSizing: 'borderBox',
  },
  sectionTitle: {
    fontSize: 12,
    padding: 10,
    backgroundColor: '#fafafa',
    borderBottom: 1,
    borderBottomColor: '#e8e8e8',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    paddingLeft: 10,
    paddingRight: 10,
    paddingTop: 10,
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'borderBox',
    paddingRight: 10,
    paddingBottom: 10,
  },
  field: {
    marginBottom: 8,
  },
  fieldLabel: {
    fontSize: 9,
    color: '#8c8c8c',
    marginBottom: 2,
    width: '100%',
  },
  fieldValue: {
    fontSize: 10.5,
    color: '#000000',
    width: '100%',
  },
  image: {
    width: 130,
    height: 130,
    border: 1,
    borderColor: '#a4a0a4',
  },
  galleryImage: {
    width: 80,
    height: 80,
    margin: 3,
    border: 1,
    borderColor: '#a4a0a4',
  },
  gallery: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  pageNumber: {
    position: 'absolute',
    fontSize: 10,
    bottom: 20,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: 'grey',
  },
});

const PAGE_CONTENT_WIDTH = 555;

const FieldValue = ({ field, isRtl }) => {
  const alignStyle = isRtl ? { textAlign: 'right' } : {};

  if (field.type === 'image') {
    return field.value
      ? <Image style={styles.image} src={field.value} />
      : <Text style={[styles.fieldValue, alignStyle]}>-</Text>;
  }
  if (field.type === 'gallery') {
    return field.value && field.value.length > 0 ? (
      <View style={styles.gallery}>
        {field.value.map(url => <Image key={url} style={styles.galleryImage} src={url} />)}
      </View>
    ) : <Text style={[styles.fieldValue, alignStyle]}>-</Text>;
  }
  return (
    <Text style={[styles.fieldValue, alignStyle]}>
      {field.value === null || field.value === undefined || field.value === '' ? '-' : String(field.value)}
    </Text>
  );
};

const Column = ({ column, width, isRtl }) => {
  const columnStyle = isRtl
    ? [styles.column, { width, paddingRight: 0, paddingLeft: 10 }]
    : [styles.column, { width }];

  const labelStyle = isRtl
    ? [styles.fieldLabel, { textAlign: 'right' }]
    : styles.fieldLabel;

  return (
    <View style={columnStyle}>
      {column.fields.map(field => (
        <View style={styles.field} key={field.label}>
          <Text style={labelStyle}>{field.label}</Text>
          <FieldValue field={field} isRtl={isRtl} />
        </View>
      ))}
    </View>
  );
};

const Row = ({ row, isRtl }) => {
  const totalWeight = row.columns.reduce((sum, column) => sum + (column.weight || 1), 0);

  const rowStyle = isRtl
    ? [styles.row, { flexDirection: 'row-reverse' }]
    : styles.row;

  const columns = isRtl ? [...row.columns].reverse() : row.columns;

  return (
    <View style={rowStyle} wrap={false}>
      {columns.map((column, columnIndex) => (
        <Column
          // eslint-disable-next-line react/no-array-index-key
          key={columnIndex}
          column={column}
          width={(PAGE_CONTENT_WIDTH * (column.weight || 1)) / totalWeight}
          isRtl={isRtl}
        />
      ))}
    </View>
  );
};

const PdfDoc = ({ title, subtitle, sections, isRtl }) => {
  const pageStyle = isRtl
    ? [styles.page, { fontFamily: 'Amiri', direction: 'rtl' }]
    : styles.page;

  const headerStyle = isRtl
    ? [styles.header, { flexDirection: 'row-reverse' }]
    : styles.header;

  const headerTextStyle = isRtl
    ? [styles.headerText, { textAlign: 'right' }]
    : styles.headerText;

  const subHeaderContainerStyle = isRtl
    ? { marginRight: 20, marginBottom: 14, textAlign: 'right'}
    : { marginLeft: 20, marginBottom: 14 };

  const sectionTitleStyle = isRtl
    ? [styles.sectionTitle, { textAlign: 'right' }]
    : styles.sectionTitle;

  return (
    <Document>
      <Page size="A4" style={pageStyle}>
        <View style={headerStyle} fixed>
          <Text style={headerTextStyle}>{title}</Text>
        </View>
        {subtitle && (
          <View style={subHeaderContainerStyle}>
            <Text style={styles.subHeaderText}>{subtitle}</Text>
          </View>
        )}
        {sections.map(section => (
          <View style={styles.section} key={section.title} wrap>
            <Text style={sectionTitleStyle}>{section.title}</Text>
            {section.rows.map((row, rowIndex) => (
              // eslint-disable-next-line react/no-array-index-key
              <Row key={rowIndex} row={row} isRtl={isRtl} />
            ))}
          </View>
        ))}
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          fixed
        />
      </Page>
    </Document>
  );
};

const GenericFormPdf = ({ title, subtitle, sections, fileName, callback }) => {
  const { i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  return (
    <div>
      <BlobProvider document={PdfDoc({ title, subtitle, sections, isRtl })}>
        {({ url, loading }) => {
          if (url && loading === false) {
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            window.URL.revokeObjectURL(url);
            callback();
          }
          return <div></div>;
        }}
      </BlobProvider>
    </div>
  );
};

export default GenericFormPdf;